import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Claves centralizadas para consistencia
export const STORAGE_KEYS = {
  SCROLL_POSITION: "tattersall-catalog-scroll-position",
  PRODUCTS_STATE: "tattersall-products-state",
  FILTERS_STATE: "tattersall-catalog-filters-state",
  CACHE_METADATA: "tattersall-cache-metadata",
  // 🆕 NUEVO: Tracking de navegación
  NAVIGATION_SOURCE: "tattersall-navigation-source",
  LAST_VISITED_PRODUCT: "tattersall-last-visited-product",
};

const useNavigationMemory = () => {
  const navigate = useNavigate();

  // 🆕 FUNCIÓN: Marcar navegación desde catálogo a producto
  const markNavigationToProduct = useCallback((productSlug) => {
    sessionStorage.setItem(
      STORAGE_KEYS.NAVIGATION_SOURCE,
      "catalog-to-product"
    );
    sessionStorage.setItem(STORAGE_KEYS.LAST_VISITED_PRODUCT, productSlug);
    console.log(`🔖 Marked navigation to product: ${productSlug}`);
  }, []);

  // 🆕 FUNCIÓN: Marcar navegación desde home con filtros
  const markNavigationFromHome = useCallback((filters) => {
    sessionStorage.setItem(STORAGE_KEYS.NAVIGATION_SOURCE, "home-with-filters");
    sessionStorage.setItem("home-navigation-filters", JSON.stringify(filters));
    console.log(`🏠 Marked navigation from home with filters:`, filters);
  }, []);

  // 🆕 FUNCIÓN: Verificar si es navegación de regreso desde producto
  const isReturningFromProduct = useCallback(() => {
    const source = sessionStorage.getItem(STORAGE_KEYS.NAVIGATION_SOURCE);
    const lastProduct = sessionStorage.getItem(
      STORAGE_KEYS.LAST_VISITED_PRODUCT
    );

    const isReturning = source === "catalog-to-product" && lastProduct;
    console.log(`🔍 Is returning from product? ${isReturning}`, {
      source,
      lastProduct,
    });

    return isReturning;
  }, []);

  // 🆕 FUNCIÓN: Verificar si es navegación nueva con filtros
  const isNewNavigationWithFilters = useCallback((currentFilters) => {
    const source = sessionStorage.getItem(STORAGE_KEYS.NAVIGATION_SOURCE);

    if (source === "home-with-filters") {
      console.log(`🆕 New navigation with filters from home`);
      return true;
    }

    // 🔧 LÓGICA MEJORADA: Verificar si hay filtros activos en URL
    const hasActiveFilters = Object.values(currentFilters || {}).some(
      (value) => {
        if (typeof value === "string") return value !== "" && value !== "Todas";
        if (Array.isArray(value)) return value.length > 0;
        return false;
      }
    );

    // 🧠 LÓGICA INTELIGENTE: Solo considerar "nueva" si:
    // 1. No hay source de navegación Y
    // 2. Hay filtros activos Y
    // 3. NO hay un caché de productos válido con los mismos filtros
    if (!source && hasActiveFilters) {
      // Verificar si hay un caché de productos válido que coincida con los filtros actuales
      const cachedProductsState = sessionStorage.getItem(
        STORAGE_KEYS.PRODUCTS_STATE
      );

      if (cachedProductsState) {
        try {
          const parsedCache = JSON.parse(cachedProductsState);
          const cachedFilters = parsedCache.lastFilters;

          // Comparar filtros actuales con los del caché
          const filtersMatch =
            JSON.stringify(currentFilters) === JSON.stringify(cachedFilters);

          if (filtersMatch) {
            console.log(
              `💾 Found matching cached filters - NOT clearing cache`
            );
            return false; // No es navegación nueva, hay caché válido
          }
        } catch (error) {
          console.warn("Error parsing cached products state:", error);
        }
      }

      console.log(`🆕 New navigation with filters detected`);
      return true;
    }

    return false;
  }, []);

  // 🆕 FUNCIÓN: Limpiar tracking de navegación
  const clearNavigationTracking = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEYS.NAVIGATION_SOURCE);
    sessionStorage.removeItem(STORAGE_KEYS.LAST_VISITED_PRODUCT);
    sessionStorage.removeItem("home-navigation-filters");
    console.log("🗑️ Navigation tracking cleared");
  }, []);

  // Función para guardar la posición de scroll antes de navegar a un producto
  const saveScrollBeforeNavigation = useCallback(
    (productSlug) => {
      const scrollY = window.scrollY;
      if (scrollY > 0) {
        sessionStorage.setItem(
          STORAGE_KEYS.SCROLL_POSITION,
          scrollY.toString()
        );
        console.log(`💾 Navigation scroll position saved: ${scrollY}`);
      }

      // También marcar la navegación
      if (productSlug) {
        markNavigationToProduct(productSlug);
      }
    },
    [markNavigationToProduct]
  );

  // Función optimizada para volver al catálogo manteniendo estado
  const navigateToCatalog = useCallback(() => {
    console.log("🔄 Navigating back to catalog with memory...");

    // Usar replace para evitar problemas de historial
    navigate("/catalogo", {
      replace: false, // Mantener en el historial para que el botón atrás funcione
    });
  }, [navigate]);

  // Función para limpiar toda la memoria de navegación
  const clearNavigationMemory = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEYS.SCROLL_POSITION);
    sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS_STATE);
    sessionStorage.removeItem(STORAGE_KEYS.FILTERS_STATE);
    clearNavigationTracking();
    console.log("🗑️ Navigation memory cleared");
  }, [clearNavigationTracking]);

  return {
    saveScrollBeforeNavigation,
    navigateToCatalog,
    clearNavigationMemory,
    // 🆕 NUEVAS FUNCIONES
    markNavigationFromHome,
    isReturningFromProduct,
    isNewNavigationWithFilters,
    clearNavigationTracking,
  };
};

export default useNavigationMemory;
